import React, { createContext, useContext, useState, useCallback } from 'react';

const UserAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const TOKEN_KEY  = 'userToken';
const USER_KEY   = 'userData';
const ADULT_KEY  = 'adultContent'; // gated behind login

export const UserAuthProvider = ({ children }) => {
  const [userToken, setUserToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)) || null; }
    catch { return null; }
  });

  // Adult-content preference — only meaningful when logged in
  const [showAdultContent, setShowAdultContent] = useState(() => {
    const hasToken = !!localStorage.getItem(TOKEN_KEY);
    return hasToken && localStorage.getItem(ADULT_KEY) === 'true';
  });

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${BACKEND_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      // Attach needsVerification so the sign-in page can show the resend CTA
      const err = new Error(data.error || 'Login failed');
      err.needsVerification = !!data.needsVerification;
      throw err;
    }
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUserToken(data.token);
    setUser(data.user);
    // Restore adult preference that was saved for this account
    const saved = localStorage.getItem(ADULT_KEY) === 'true';
    setShowAdultContent(saved);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.setItem(ADULT_KEY, 'false'); // Reset on logout
    setUserToken(null);
    setUser(null);
    setShowAdultContent(false);
  }, []);

  // Toggle adult content — only works when logged in
  const toggleAdultContent = useCallback((value) => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    const next = value !== undefined ? Boolean(value) : !showAdultContent;
    localStorage.setItem(ADULT_KEY, String(next));
    setShowAdultContent(next);
  }, [showAdultContent]);

  return (
    <UserAuthContext.Provider value={{
      isLoggedIn: !!userToken,
      userToken,
      user,
      login,
      logout,
      showAdultContent,
      toggleAdultContent,
    }}>
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = () => useContext(UserAuthContext);

